// CRM palette — the SINGLE source of truth. Change here, updates everywhere.
// Atoms/molecules/organisms MUST read from this file. No hardcoded hexes elsewhere.

export const PALETTES = {
  // Matches the DashboardV2 action blue used by Projects, Team and Goals, so
  // CRM reads as the same product rather than a bolted-on module.
  teamgrid: {
    name: "TeamGrid (default)",
    primary: "#4273D1",
    primaryHover: "#335FB5",
    primaryRing: "rgba(66, 115, 209, 0.28)",
    primarySoft: "#E9F1FE",
    primaryText: "#FFFFFF",
  },
  indigo: {
    name: "Indigo (default)",
    primary: "#4F46E5",
    primaryHover: "#4338CA",
    primaryRing: "rgba(79, 70, 229, 0.35)",
    primarySoft: "rgba(79, 70, 229, 0.12)",
    primaryText: "#FFFFFF",
  },
  teal: {
    name: "Teal (TG brand)",
    primary: "#00A89B",
    primaryHover: "#008C81",
    primaryRing: "rgba(0, 168, 155, 0.35)",
    primarySoft: "rgba(0, 209, 193, 0.14)",
    primaryText: "#FFFFFF",
  },
  graphite: {
    name: "Graphite (Linear)",
    primary: "#1F2937",
    primaryHover: "#111827",
    primaryRing: "rgba(31, 41, 55, 0.35)",
    primarySoft: "rgba(31, 41, 55, 0.08)",
    primaryText: "#FFFFFF",
  },
  violet: {
    name: "Violet",
    primary: "#7C3AED",
    primaryHover: "#6D28D9",
    primaryRing: "rgba(124, 58, 237, 0.35)",
    primarySoft: "rgba(124, 58, 237, 0.12)",
    primaryText: "#FFFFFF",
  },
  blue: {
    name: "Royal Blue",
    primary: "#2563EB",
    primaryHover: "#1D4ED8",
    primaryRing: "rgba(37, 99, 235, 0.35)",
    primarySoft: "rgba(37, 99, 235, 0.12)",
    primaryText: "#FFFFFF",
  },
  rose: {
    name: "Rose",
    primary: "#E11D48",
    primaryHover: "#BE123C",
    primaryRing: "rgba(225, 29, 72, 0.35)",
    primarySoft: "rgba(225, 29, 72, 0.12)",
    primaryText: "#FFFFFF",
  },
};

// Status colors — semantic, do NOT change per palette. Values mirror the
// DashboardV2 status tokens so a "won" pill here is the same green as a
// positive trend on Projects.
export const STATUS = {
  success: "#50AB5B",
  successSoft: "rgba(80, 171, 91, 0.14)",
  warning: "#A29B4E",
  warningSoft: "rgba(162, 155, 78, 0.16)",
  danger: "#E5484D",
  dangerSoft: "rgba(229, 72, 77, 0.13)",
  info: "#4273D1",
  infoSoft: "rgba(66, 115, 209, 0.12)",
  neutral: "#757677",
  neutralSoft: "rgba(117, 118, 119, 0.12)",
};

// Workflow action colors — SOLID fills, white text on top. These are the only
// place in the app where a chip is filled rather than tinted, because a workflow
// row has to be read at a glance: the colour IS the channel. Deliberately not
// derived from STATUS (a WhatsApp message is not a "success") and deliberately
// not tinted (a wall of pastel shades is unreadable at row density).
export const ACTION_COLORS = {
  whatsapp: "#1FA855",
  email: "#2563EB",
  pdf: "#7C3AED",
  call: "#EA580C",
  meeting: "#0891B2",
  task: "#475569",
  wait: "#94A3B8",
};

// The "when" chip. One flat ink for every trigger, so colour stays meaningful:
// the trigger states a fact, the actions carry the palette.
export const TRIGGER_COLOR = "#1F2937";

export const DEFAULT_PALETTE = "teamgrid";

// Apply a palette by writing CSS vars. We write to BOTH the scope element
// (kept for back-compat — legacy callers passed a per-route container) AND
// to document.documentElement so portaled UI like the Plivo call widget
// (mounted at document.body, outside the CRM provider tree) can still
// resolve `var(--crm-*)`. Without the root write, those CSS vars are
// undefined in the portal subtree and styles that depend on them render
// blank (e.g. danger-coloured hangup button → invisible).
export function applyPalette(scopeEl, paletteKey) {
  const p = PALETTES[paletteKey] || PALETTES[DEFAULT_PALETTE];
  const targets = [];
  if (typeof document !== "undefined" && document.documentElement) {
    targets.push(document.documentElement);
  }
  if (scopeEl && !targets.includes(scopeEl)) targets.push(scopeEl);
  for (const el of targets) {
    el.style.setProperty("--crm-primary", p.primary);
    el.style.setProperty("--crm-primary-hover", p.primaryHover);
    el.style.setProperty("--crm-primary-ring", p.primaryRing);
    el.style.setProperty("--crm-primary-soft", p.primarySoft);
    el.style.setProperty("--crm-primary-text", p.primaryText);
    el.style.setProperty("--crm-success", STATUS.success);
    el.style.setProperty("--crm-success-soft", STATUS.successSoft);
    el.style.setProperty("--crm-warning", STATUS.warning);
    el.style.setProperty("--crm-warning-soft", STATUS.warningSoft);
    el.style.setProperty("--crm-danger", STATUS.danger);
    el.style.setProperty("--crm-danger-soft", STATUS.dangerSoft);
    el.style.setProperty("--crm-info", STATUS.info);
    el.style.setProperty("--crm-info-soft", STATUS.infoSoft);
    el.style.setProperty("--crm-neutral", STATUS.neutral);
    el.style.setProperty("--crm-neutral-soft", STATUS.neutralSoft);
  }
}
